import React from "react";
import { Box, Typography, Checkbox } from "@mui/material";
import { W } from "./wizardTokens";
import FlagBanner from "./FlagBanner";
import TestQuestionReview from "./TestQuestionReview";
import { useConfigList } from "../../../hooks/useConfigList";
import { computeMissingFields, countUnresolvedFlags, nextLocalId, totalPoints, VALIDATION_TYPES } from "./reviewModel";

const DEFAULT_CATEGORIES = ["Firefighter", "EMS", "Hazmat", "Rescue", "Fire Officer", "General"];

const fieldSx = (invalid) => ({
  width: "100%", height: 40, px: "12px", border: `1px solid ${invalid ? W.danger : W.line}`, borderRadius: "6px",
  font: "inherit", fontSize: "14.5px", bgcolor: "#fff", "&:focus": { outline: "none", borderColor: invalid ? W.danger : W.primary },
});

function SkillItemsTable({ draft, onChange }) {
  const updItem = (idx, patch) => onChange({ list: draft.list.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
  const total = totalPoints(draft.list, "pointValue");
  return (
    <Box sx={{ border: `1px solid ${W.line}`, borderRadius: "10px", overflow: "hidden" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "28px minmax(0,1fr) 116px 70px 72px 32px", gap: "10px", p: "10px 12px", fontSize: "10px", fontWeight: 700, letterSpacing: ".025em", textTransform: "uppercase", color: W.ink3, borderBottom: `1px solid ${W.line}` }}>
        <span>#</span><span>Step</span><span>Type</span><span style={{ textAlign: "right" }}>Points</span><span style={{ textAlign: "center" }}>Critical</span><span />
      </Box>
      {draft.list.map((s, i) => (
        <Box key={s.id || i} sx={{ borderTop: i ? `1px solid ${W.line}` : "none", bgcolor: s.needsReview ? W.dangerBg : "transparent" }}>
          {s.sectionTitle && s.sectionTitle !== draft.list[i - 1]?.sectionTitle && (
            <Box data-testid="review-section-heading" sx={{ p: "8px 12px", fontSize: "12px", fontWeight: 700, color: W.ink2 || W.ink3, bgcolor: W.line }}>
              {s.sectionTitle}
            </Box>
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: "28px minmax(0,1fr) 116px 70px 72px 32px", gap: "10px", alignItems: "center", p: "8px 12px" }}>
            <Box sx={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: s.needsReview ? W.danger : W.ink3 }}>{String(i + 1).padStart(2, "0")}</Box>
            <Box component="input" value={s.criteria || ""} aria-label={`Step ${i + 1}`} onChange={(e) => updItem(i, { criteria: e.target.value })}
              sx={{ height: 36, px: "10px", border: `1px solid ${W.line}`, borderRadius: "6px", font: "inherit", fontSize: "14px", bgcolor: "#fff" }} />
            <Box component="select" aria-label={`Type for step ${i + 1}`} value={s.validationType || ""} onChange={(e) => updItem(i, { validationType: e.target.value })}
              sx={{ height: 36, px: "6px", border: `1px solid ${VALIDATION_TYPES.includes(s.validationType) ? W.line : W.danger}`, borderRadius: "6px", font: "inherit", fontSize: "13px", bgcolor: "#fff" }}>
              <option value="" disabled>Choose…</option>
              {VALIDATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </Box>
            <Box component="input" value={s.pointValue ?? ""} aria-label={`Points for step ${i + 1}`} onChange={(e) => updItem(i, { pointValue: e.target.value === "" ? "" : Number(e.target.value) })}
              sx={{ height: 36, px: "8px", textAlign: "right", border: `1px solid ${W.line}`, borderRadius: "6px", font: "inherit", fontSize: "14px", bgcolor: "#fff" }} />
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Checkbox size="small" checked={!!s.isCritical} onChange={(e) => updItem(i, { isCritical: e.target.checked })} inputProps={{ "aria-label": `Critical step ${i + 1}` }} />
            </Box>
            <Box component="button" type="button" aria-label={`Delete step ${i + 1}`} onClick={() => onChange({ list: draft.list.filter((_, k) => k !== i) })}
              sx={{ width: 28, height: 28, border: 0, bgcolor: "transparent", color: W.ink3, cursor: "pointer", "&:hover": { color: W.danger } }}>✕</Box>
          </Box>
          {s.needsReview && (
            <Box sx={{ p: "0 12px 12px 50px" }}>
              <FlagBanner note={s.reviewNote} quote={s.sourceQuote} onClear={() => updItem(i, { needsReview: false })} />
            </Box>
          )}
        </Box>
      ))}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "10px 12px", borderTop: `1px solid ${W.line}` }}>
        <Box component="button" type="button" onClick={() => onChange({ list: [...draft.list, { id: nextLocalId(), criteria: "", validationType: "Pass/Fail", pointValue: 0, isCritical: false }] })}
          sx={{ font: "inherit", fontSize: "13px", fontWeight: 700, color: W.ai, border: 0, bgcolor: "transparent", cursor: "pointer" }}>
          + Add step
        </Box>
        <Typography sx={{ fontSize: "13px" }}>Total <b>{total}</b> pts</Typography>
      </Box>
    </Box>
  );
}

function DraftStatus({ draft }) {
  const missing = computeMissingFields(draft);
  const flags = countUnresolvedFlags(draft);
  if (!draft.include) return <Typography sx={{ fontSize: "12px", color: W.ink4 }}>Won't be saved</Typography>;
  if (missing.length) return <Typography sx={{ fontSize: "12px", color: W.danger }}>Needs info</Typography>;
  if (flags) return <Typography sx={{ fontSize: "12px", color: W.danger }}>⚠ {flags} to check</Typography>;
  return <Typography sx={{ fontSize: "12px", color: W.success }}>Ready</Typography>;
}

export default function ReviewStep({ drafts, sel, setSel, onChangeDraft }) {
  const categories = useConfigList("SKILL_CATEGORY", DEFAULT_CATEGORIES);
  const draft = drafts[sel];
  if (!draft) return null;
  const patch = (p) => onChangeDraft(sel, { ...p, dirty: true });
  const setField = (field, value) => patch({ payload: { ...draft.payload, [field]: value } });
  const missing = computeMissingFields(draft);
  const missingTitle = missing.includes("title");
  const missingPassingScore = missing.includes("passingScore");
  const missingList = missing.includes("items") || missing.includes("questions");
  const categoryOptions = draft.payload.category && !categories.includes(draft.payload.category)
    ? [draft.payload.category, ...categories] : categories;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "220px minmax(0,1fr)" }, gap: "20px", alignItems: "start" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {drafts.map((d, i) => (
          <Box key={d.rowId} onClick={() => setSel(i)}
            sx={{ display: "grid", gridTemplateColumns: "20px minmax(0,1fr)", gap: "10px", alignItems: "center", p: "10px 12px", borderRadius: "10px", cursor: "pointer", bgcolor: i === sel ? W.aiBg : "transparent", border: `1px solid ${i === sel ? W.aiLine : "transparent"}`, "&:hover": { bgcolor: i === sel ? W.aiBg : "#faf9fb" } }}>
            <Checkbox size="small" checked={d.include} onClick={(e) => e.stopPropagation()}
              onChange={(e) => onChangeDraft(i, { include: e.target.checked })} inputProps={{ "aria-label": `Include ${d.payload.title || d.fileName}` }} sx={{ p: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "13.5px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.payload.title || d.fileName}</Typography>
              <DraftStatus draft={d} />
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", minWidth: 0 }}>
        <Box sx={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <Box component="span" sx={{ fontSize: "11.5px", fontWeight: 700, px: "8px", py: "2px", borderRadius: "999px", bgcolor: W.aiBg, color: W.ai, border: `1px solid ${W.aiLine}` }}>AI draft</Box>
          <Typography sx={{ fontSize: "12.5px", color: W.ink3 }}>From {draft.fileName}</Typography>
          {draft.isDuplicate && <Typography sx={{ fontSize: "12.5px", color: W.warn, fontWeight: 600 }}>Possible duplicate of an existing item</Typography>}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0,1fr) 160px 110px" }, gap: "12px" }}>
          <Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: "6px" }}>Title</Typography>
            <Box component="input" aria-label="Title" value={draft.payload.title || ""} onChange={(e) => setField("title", e.target.value)} sx={fieldSx(missingTitle)} />
            {missingTitle && <Typography sx={{ fontSize: "11.5px", color: W.danger, mt: "4px" }}>AI couldn't find this — enter it manually.</Typography>}
          </Box>
          <Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: "6px" }}>Category</Typography>
            <Box component="select" aria-label="Category" value={draft.payload.category || ""} onChange={(e) => setField("category", e.target.value)} sx={{ ...fieldSx(false), px: "8px" }}>
              <option value="">None</option>
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, mb: "6px" }}>Pass score</Typography>
            <Box component="input" aria-label="Pass score" inputMode="numeric" value={draft.payload.passingScore ?? ""}
              onChange={(e) => setField("passingScore", e.target.value === "" ? "" : Number(e.target.value))} sx={fieldSx(missingPassingScore)} />
            {missingPassingScore && <Typography sx={{ fontSize: "11.5px", color: W.danger, mt: "4px" }}>Required.</Typography>}
          </Box>
        </Box>

        {missingList && (
          <Box sx={{ p: "12px 14px", borderRadius: "10px", bgcolor: W.dangerBg, border: `1px solid ${W.danger}`, fontSize: "13px", color: W.danger, fontWeight: 600 }}>
            AI couldn't find any {draft.type === "SKILL_SHEET" ? "steps" : "questions"} — add at least one manually below.
          </Box>
        )}
        {missing.includes("validationType") && (
          <Box sx={{ p: "12px 14px", borderRadius: "10px", bgcolor: W.dangerBg, border: `1px solid ${W.danger}`, fontSize: "13px", color: W.danger, fontWeight: 600 }}>
            AI couldn't tell if some steps are Pass/Fail or Observation — choose a type for each highlighted step.
          </Box>
        )}
        {missing.includes("answers") && (
          <Box sx={{ p: "12px 14px", borderRadius: "10px", bgcolor: W.dangerBg, border: `1px solid ${W.danger}`, fontSize: "13px", color: W.danger, fontWeight: 600 }}>
            Some questions have no correct answer — open them and mark one.
          </Box>
        )}

        {draft.type === "SKILL_SHEET"
          ? <SkillItemsTable draft={draft} onChange={patch} />
          : <TestQuestionReview key={draft.rowId} draft={draft} onChange={patch} />}
      </Box>
    </Box>
  );
}
